import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye-2-icon.svg';
import { ReactComponent as DocumentFileIcon } from '../../assets/icons/tables/document-file.svg';
import { useAppBranding } from '../../config/branding';
import { ChargingStationInterFace } from '../../interface/charging-station.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import {
  fetchAllUnapprovedStation,
  fetchPendingNotification,
} from '../../redux/slices/UnapprovedStationManagementSlice';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import chargingStation from '../../utils/api/charging-station';
import electricityPrice from '../../utils/api/electricity-price';
import MyButton from '../button/Button';
import CustomTable from '../custom-table/CustomTable';
import ModalBox from '../modal-box/ModalBox';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  height?: number;

  onDeleteStation?: (stationId?: string) => void;
  usageCostView?: 'pending' | 'approved' | 'rejected';
}

interface ChargingPointProperties {
  chargingPointSn: string;
  connectorType: string; // use union if more types exist
  isDeviceOffline: boolean;
  _id: string;
  id: string;
}

/**
 * View the file based on its type
 * @param param0 component props
 * @param param0.file file object
 * @param param0.extraCount extra count of files
 * @returns JSX element
 */
function FilePreview({ file, extraCount = 0 }: { file: any; extraCount?: number }) {
  const { t } = useTranslation('sidePanel');

  let fileTypeView;

  if (file.fileType.includes('image')) {
    fileTypeView = <img src={file.signedUrl} alt="" className="selected-image" />;
  } else if (file.fileType.includes('video')) {
    fileTypeView = (
      <video controls className="selected-video">
        <source src={file.signedUrl} type={file.fileType} />
        <track src="path/to/captions.vtt" kind="captions" label={t('englishCaptions')} default />
        {t('browserNotVideoTag')}{' '}
      </video>
    );
  }

  return (
    <div className="single-selected-wrap">
      {fileTypeView}
      {extraCount && extraCount > 0 ? (
        <div className="extra-count-overlay">
          <span>+{extraCount}</span>
        </div>
      ) : null}
    </div>
  );
}
FilePreview.defaultProps = {
  extraCount: 0,
};

/**
 * PDF/Document Chip Component
 */
function DocumentChip({
  label,
  file = undefined,
  onClick,
}: {
  label: string;
  file?: any;
  onClick: () => void;
}) {
  const { t } = useTranslation('sidePanel');
  return (
    <div className="document-chip-wrap">
      <span className="doc-label">{label}</span>
      <div className="doc-file-box">
        <div className="doc-icon-name">
          <DocumentFileIcon />
          <span className="file-name">{file ? file.fileName : t('noFile')}</span>
        </div>
        {file && (
          <div className="doc-view-icon" role="presentation" onClick={onClick}>
            <EyeIcon color="var(--primary-color)" />
          </div>
        )}
      </div>
    </div>
  );
}
DocumentChip.defaultProps = {
  file: undefined,
};

/**
 * Global Setting Component
 */

/**
 * Station side panel
 * @param properties component props
 * @returns JSX element
 */
function StationSidePanel(properties: StationSidePanelProperties) {
  const { t, i18n } = useTranslation('sidePanel');
  const branding = useAppBranding();
  const { fetchIdFrom, id, onDeleteStation, usageCostView = 'pending', height } = properties;
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();

  // Selectors for different states
  const unapprovedState = useAppSelector((state) => state.unapprovedStation);

  // Document viewing state
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    type: string;
    name: string;
  } | null>(null);

  /**
   * Handle view document
   */
  const handleViewDocument = (file: any) => {
    if (file?.signedUrl || file?.url) {
      setSelectedDocument({
        url: file.signedUrl,
        type: file.fileType || 'application/pdf', // Fallback or logic
        name: file.fileName || t('document'),
      });
      setIsDocumentModalOpen(true);
    }
  };

  /**
   * Handle close document modal
   */
  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  const [chargingStationData, setChargingStationData] = useState<ChargingStationInterFace>();
  const [chargingPointData, setChargingPointData] = useState<ChargingPointProperties[]>([]);
  const [rejectReason, setRejectReason] = useState('');
  // const [priceRequestData, setPriceRequestData] = useState<ElectricityPrice | null>(null);
  const [priceRejectReason, setPriceRejectReason] = useState('');
  const [localUsageCostView, setLocalUsageCostView] = useState<
    'pending' | 'rejectForm' | 'approved' | 'rejected'
  >('pending');
  const [localPriceView, setLocalPriceView] = useState<
    'pending' | 'rejectForm' | 'approved' | 'rejected'
  >('pending');

  useEffect(() => {
    if (usageCostView) {
      setLocalUsageCostView(usageCostView);
    }
  }, [usageCostView]);

  // const [partLength, setPartLength] = useState<number>(1);

  const amenitiesDetails = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: (
        <branding.icons.GasActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('wifi'),
      key: 'Free Wifi',
      svgIconActive: (
        <branding.icons.WifiActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: (
        <branding.icons.RestroomActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('lounge'),
      key: 'Lounge',
      svgIconActive: (
        <branding.icons.LoungeActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('caffee'),
      key: 'Cafe',
      svgIconActive: (
        <branding.icons.CafeActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: (
        <branding.icons.ShoppingActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: (
        <branding.icons.RestaurantActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: (
        <branding.icons.RelaxingActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
    // {
    //   name: 'Locker',
    //   key: 'Locker',
    //   svgIconActive: <branding.icons.LockerActive color="var(--primary-color)" fill="var(--primary-color)" />,
    // },
    // {
    //   name: 'Work Shop',
    //   key: 'Work Shop',
    //   svgIconActive: <branding.icons.WorkshopActive color="var(--primary-color)" fill="var(--primary-color)" />,
    // },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: (
        <branding.icons.CarWashActive color="var(--primary-color)" fill="var(--primary-color)" />
      ),
    },
  ];

  const column = [
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ChargingPointProperties,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'isDeviceOffline' as keyof ChargingPointProperties,
      sortAllow: false,
      /**
       * Function
       * @param row row data
       * @returns JSX element
       */
      customData: (row: ChargingPointProperties) => (
        <p>{row.isDeviceOffline ? t('offline') : t('online')}</p>
      ),
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof ChargingPointProperties,
      sortAllow: false,
    },
  ];

  /**
   * Function to fetch station details
   * @param stationId station id
   */
  const handleFetchStationDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingStationById(stationId)
      .then((response) => {
        setChargingStationData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to fetch station details
   * @param stationId station id
   */
  const handleFetchDeviceDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingPointByStationId(stationId)
      .then((response) => {
        setChargingPointData(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Fetch electricity price details
   * @param stationId
   */
  // /**
  //  * Fetch electricity price details
  //  * @param stationId
  //  */
  // const handleFetchElectricityPrice = async (stationId: string) => {
  //   // Only fetch if we are in a view that expects price data
  //   if (!['pending', 'approved', 'rejected'].includes(usageCostView)) return;

  //   try {
  //     const statusToFetch = usageCostView;

  //     const response = await electricityPrice.fetchUnapprovedElectricityPrice({
  //       chargingStation: stationId,
  //       status: statusToFetch,
  //       limit: 1, // Get the latest one
  //     });

  //     if (response?.data?.results?.length > 0) {
  //       setPriceRequestData(response.data.results[0]);
  //     } else {
  //       setPriceRequestData(null);
  //     }
  //   } catch (error) {
  //     console.error('Failed to fetch electricity price:', error);
  //   }
  // };

  /**
   * Refresh data function
   */
  const refreshData = () => {
    if (usageCostView === 'pending' && unapprovedState) {
      const { filters, limit, page, sortBy, sortField } = unapprovedState;
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(fetchAllUnapprovedStation({ ...queryParameters, limit, page, sortBy, sortField }));
    }
  };

  useEffect(() => {
    // const length = chargingStationData?.medias?.length || 3;
    // setPartLength(Math.ceil(length / 3)); // NOT USING GRID COLUMNS LOGIC ANYMORE
  }, [chargingStationData?.medias]);

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('stationId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
    handleFetchDeviceDetails(stationId);
    // handleFetchElectricityPrice(stationId);
  }, [searchParameters, id]);

  /**
   * Handle approve action
   */
  const handleApprove = async () => {
    if (!chargingStationData?._id) return;
    try {
      await chargingStation.statusChangeChargingStation(chargingStationData._id, {
        submissionStatus: 'approved',
      });
      refreshData();
      // Close panel
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters);
      dispatch(fetchPendingNotification());
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('errorMessage'),
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Handle reject click
   */
  const handleRejectClick = () => {
    setLocalUsageCostView('rejectForm');
  };

  /**
   * Handle cancel reject
   */
  const handleCancelReject = () => {
    setLocalUsageCostView('pending');
    setRejectReason('');
  };

  /**
   * Handle proceed with reject
   */
  const handleProceedReject = async () => {
    if (!chargingStationData?._id) return;
    try {
      await chargingStation.statusChangeChargingStation(chargingStationData._id, {
        submissionStatus: 'rejected',
        reason: rejectReason,
      });
      refreshData();
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters);
      dispatch(fetchPendingNotification());
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('errorMessage'),
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Handle approve action for Price
   */
  const handleApprovePrice = async () => {
    if (!chargingStationData?.electricityPriceData?._id) return;
    try {
      await electricityPrice.updateStatusElectricityPriceHistory(
        chargingStationData?.electricityPriceData?._id,
        {
          status: 'approved',
        },
      );
      refreshData();
      // Close panel
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('errorMessage'),
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Handle reject click for Price
   */
  const handleRejectPriceClick = () => {
    setLocalPriceView('rejectForm');
  };

  /**
   * Handle cancel reject for Price
   */
  const handleCancelRejectPrice = () => {
    setLocalPriceView('pending');
    setPriceRejectReason('');
  };

  /**
   * Handle proceed with reject for Price
   */
  const handleProceedRejectPrice = async () => {
    if (!chargingStationData?.electricityPriceData?._id) return;
    try {
      await electricityPrice.updateStatusElectricityPriceHistory(
        chargingStationData?.electricityPriceData?._id,
        {
          status: 'rejected',
          reason: priceRejectReason,
        },
      );
      refreshData();
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('errorMessage'),
          type: 'error',
          code: 0,
        }),
      );
    }
  };
  return (
    <div className="station-side-panel-wrap" dir={i18n.dir()}>
      <ModalBox
        title={selectedDocument?.name || t('documentPreview')}
        open={isDocumentModalOpen}
        closeModal={handleCloseDocumentModal}
        header
        rightCorner=""
        position="center"
        content={
          <div className="document-preview-container">
            {(() => {
              if (!selectedDocument?.url)
                return <p className="document-preview-text">{t('noDocumentToDisplay')}</p>;

              if (selectedDocument.type.includes('image')) {
                return (
                  <img
                    src={selectedDocument.url}
                    alt={t('document')}
                    className="document-preview-image"
                  />
                );
              }

              return (
                <iframe
                  src={selectedDocument.url}
                  title={t('documentViewer')}
                  className="document-preview-iframe"
                />
              );
            })()}
          </div>
        }
      />
      <div
        className="station-side-panel-container"
        style={{ height: `${height}vmin` }}
        dir={i18n.dir()}>
        <div className="station-data-container">
          {/* Header Section */}
          <div className="panel-custom-header">
            <h3>{t('stationDetails')}</h3>
            <div className="header-actions">
              {onDeleteStation && (
                <DeleteIcon
                  className="delete-icon"
                  onClick={() => {
                    onDeleteStation?.(chargingStationData?._id);
                  }}
                />
              )}
            </div>
          </div>

          {/* Identity Section */}
          <div className="station-identity-section">
            <h2 className="station-name">{chargingStationData?.name || '-'}</h2>
          </div>
          <p className="facility-name">
            <span className="station-name">{t('facilityAccount')} :</span>{' '}
            {chargingStationData?.facilityName || chargingStationData?.accountCustomId}
          </p>
          <div className="divider-line" />

          {/* Basic Information */}
          <div className="section-block basic-info-block">
            <h4>{t('basicInformation')}</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">{t('electricityType')}:</span>
                <span className="value">
                  {chargingStationData?.electricityContractType
                    ? t(chargingStationData.electricityContractType.toLowerCase())
                    : '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">{t('openingHours')}:</span>
                <span className="value">
                  {chargingStationData?.opensAt && chargingStationData?.closesAt
                    ? `${FormatUtcString(ConvertUTCtoHHMM(chargingStationData.opensAt))
                        .replace('AM', t('am'))
                        .replace('PM', t('pm'))} - ${FormatUtcString(
                        ConvertUTCtoHHMM(chargingStationData.closesAt),
                      )
                        .replace('AM', t('am'))
                        .replace('PM', t('pm'))}`
                    : '-'}
                </span>
              </div>
              {/* <div className="info-item">
                <span className="label">Address:</span>
                <span className="value">{chargingStationData?.address || '-'}</span>
              </div> */}
              <div className="info-item">
                <span className="label">{t('utilityCost')}:</span>
                <span className="value">
                  {chargingStationData?.electricityCostByUtilityProvider || '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">{t('stationLocation')}:</span>
                <span className="value">{chargingStationData?.address || '-'}</span>
              </div>
            </div>
          </div>

          <div className="divider-line" />

          {/* Documents */}
          <div className="section-block documents-block">
            <h4>{t('documents')}</h4>
            <div className="documents-row">
              <DocumentChip
                label={t('landDocument')}
                file={chargingStationData?.landOwnerShipDocument?.[0]}
                onClick={() => handleViewDocument(chargingStationData?.landOwnerShipDocument?.[0])}
              />
              <DocumentChip
                label={t('ebReports')}
                file={chargingStationData?.ebReport?.[0]}
                onClick={() => handleViewDocument(chargingStationData?.ebReport?.[0])}
              />
            </div>
          </div>

          <div className="divider-line" />

          {chargingStationData &&
            localUsageCostView !== 'approved' &&
            (chargingStationData as any)?.submission?.status !== 'approved' && (
              <div className="section-block usage-cost-block">
                <h4>{t('stationVerification')}</h4>
                <div className="usage-cost-card">
                  {/* Action Buttons for Pending */}
                  {localUsageCostView === 'pending' &&
                    (chargingStationData as any)?.submission?.status !== 'rejected' && (
                      <div className="button-row">
                        <MyButton
                          label={t('reject')}
                          buttonType="danger-outline"
                          onClickFunc={handleRejectClick}
                        />

                        <MyButton
                          label={t('approve')}
                          buttonType="primary"
                          onClickFunc={handleApprove}
                          disableHoverEffect={false}
                        />
                      </div>
                    )}

                  {/* Reject Form */}
                  {localUsageCostView === 'rejectForm' && (
                    <div className="reject-form-container">
                      <textarea
                        className="reject-reason-input"
                        placeholder={t('reasonForReject')}
                        value={rejectReason}
                        onChange={(event) => setRejectReason(event.target.value)}
                      />
                      <div className="button-row">
                        <MyButton
                          label={t('cancel')}
                          buttonType="outline"
                          onClickFunc={handleCancelReject}
                        />
                        <MyButton
                          label={t('proceed')}
                          buttonType="error"
                          onClickFunc={handleProceedReject}
                        />
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason Display */}
                  {(chargingStationData?.submission?.status === 'rejected' ||
                    localUsageCostView === 'rejected') && (
                    <div className="rejection-reason-container">
                      <span className="label">{t('rejectionReason')}:</span>
                      <p className="value">
                        {(chargingStationData as any)?.submission?.reason ||
                          (chargingStationData as any)?.reason ||
                          '-'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

          {chargingStationData?.electricityPriceData && (
            <div className="section-block electricity-price-container">
              <div className="usage-cost-card">
                <div className="header">
                  <p className="title">{t('usageCostPerKwh')}</p>
                </div>

                <div className="price-details-content">
                  <p className="date-text">
                    {t('date')}:{' '}
                    <span className="value">
                      {ConvertUTCtoDate(chargingStationData?.electricityPriceData?.createdAt)}
                    </span>
                  </p>

                  <p className="price-item">
                    {chargingStationData?.electricityPriceData?.status === 'approved'
                      ? t('currentPrice')
                      : t('updatedPrice')}{' '}
                    <span className="value">
                      {chargingStationData?.electricityPriceData?.electricityCostByUtilityProvider}{' '}
                      {t('sar')}
                    </span>
                  </p>

                  <p className="price-item">
                    {t('previousPrice')}:{' '}
                    <span className="value">
                      {
                        chargingStationData?.electricityPriceData
                          ?.existingElectricityCostByUtilityProvider
                      }{' '}
                      {t('sar')}
                    </span>
                  </p>
                </div>

                {/* Action Buttons for Pending */}
                {chargingStationData?.electricityPriceData?.status === 'pending' &&
                  localPriceView !== 'rejectForm' && (
                    <div className="button-row">
                      <MyButton
                        label={t('reject')}
                        buttonType="error"
                        onClickFunc={handleRejectPriceClick}
                      />
                      <MyButton
                        label={t('approve')}
                        buttonType="primary"
                        onClickFunc={handleApprovePrice}
                      />
                    </div>
                  )}

                {/* Reject Form */}
                {localPriceView === 'rejectForm' && (
                  <div className="reject-form-container">
                    <textarea
                      className="reject-reason-input"
                      placeholder={t('reasonForReject')}
                      value={priceRejectReason}
                      onChange={(event) => setPriceRejectReason(event.target.value)}
                    />
                    <div className="button-row">
                      <MyButton
                        label={t('cancel')}
                        buttonType="outline"
                        onClickFunc={handleCancelRejectPrice}
                      />
                      <MyButton
                        label={t('proceed')}
                        buttonType="error"
                        onClickFunc={handleProceedRejectPrice}
                      />
                    </div>
                  </div>
                )}

                {/* Rejection Reason Display */}
                {chargingStationData?.electricityPriceData?.status === 'rejected' && (
                  <div className="rejection-reason-container">
                    <span className="label">{t('rejectionReason')}:</span>
                    <p className="value">
                      {chargingStationData?.electricityPriceData?.reason || '-'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="divider-line" />

          {/* Media */}
          <div className="section-block media-block">
            <h4>{t('mediaAndAmenities')}</h4>
            <div className="media-grid-view">
              {chargingStationData?.medias?.slice(0, 4).map((media, index) => {
                const isFourth = index === 3;
                const remaining = (chargingStationData?.medias?.length || 0) - 4;
                return (
                  <FilePreview
                    key={media.url}
                    file={media}
                    extraCount={isFourth && remaining > 0 ? remaining : 0}
                  />
                );
              })}
            </div>
          </div>

          {/* Amenities */}
          {chargingStationData?.amenities?.length ? (
            <div className="section-block amenities-block">
              <h4>{t('amenities')}</h4>
              <div className="amenities-list">
                {chargingStationData?.amenities?.map((eachData) => {
                  const amenityToShow =
                    amenitiesDetails?.find((amenity) => amenity.key === eachData) ||
                    amenitiesDetails?.[0];

                  return (
                    <div className="single-amenity-item" key={eachData}>
                      <div className="icon-box">{amenityToShow?.svgIconActive}</div>
                      <span className="amenity-name">{amenityToShow?.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="divider-line" />
          {/* Device Info Table */}
          {usageCostView !== 'pending' && usageCostView !== 'rejected' && (
            <div className="section-block device-info-block">
              <h4>{t('deviceInfo')}</h4>
              <div className="table-container">
                <CustomTable
                  header={column}
                  data={chargingPointData}
                  serialNo
                  actionButton={false}
                  limit={0}
                  page={0}
                  loading={false}
                  totalPages={0}
                  totalResults={0}
                  pageLimitChange={(value: any) => {
                    console.log(value);
                  }}
                  sizeChangeButtonRequired
                  disablePagination
                  disableHeader
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Document Modal */}
    </div>
  );
}

export default StationSidePanel;
